import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, RouterProvider } from "react-router";
import { ApiError } from "./api";
import { AuthProvider } from "./auth/AuthContext";
import { AppShell } from "./components/AppShell";
import { RequireAuth } from "./components/RequireAuth";
import { AccountPage } from "./pages/AccountPage";
import { ChatsPage } from "./pages/ChatsPage";
import { CirclePage } from "./pages/CirclePage";
import { CirclesPage } from "./pages/CirclesPage";
import { DeleteAccountPage } from "./pages/DeleteAccountPage";
import { DrivingPage } from "./pages/DrivingPage";
import { EarlyAccessPage } from "./pages/EarlyAccessPage";
import { EmergencyContactPage } from "./pages/EmergencyContactPage";
import { HistoryPage } from "./pages/HistoryPage";
import { JoinPage } from "./pages/JoinPage";
import { LoginPage } from "./pages/LoginPage";
import { MapPage } from "./pages/MapPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { PlacesPage } from "./pages/PlacesPage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { NotificationsPage } from "./pages/NotificationsPage";
import { SignupPage } from "./pages/SignupPage";
import { SosLinkPage } from "./pages/SosLinkPage";
import { TermsPage } from "./pages/TermsPage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Client errors (401 after a failed refresh, 403, 404) won't fix themselves.
      retry: (failures, error) => !(error instanceof ApiError && error.status < 500) && failures < 2,
    },
  },
});

const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  { path: "/signup", element: <SignupPage /> },
  // Public: linked from the Play listing and the apps, signed in or not.
  { path: "/privacy", element: <PrivacyPage /> },
  { path: "/delete-account", element: <DeleteAccountPage /> },
  { path: "/terms", element: <TermsPage /> },
  // Public: emailed to emergency contacts, who have no account.
  { path: "/emergency-contact/:token", element: <EmergencyContactPage /> },
  { path: "/sos/:token", element: <SosLinkPage /> },
  // Public: the early access confirmation link from the landing page's form.
  { path: "/early-access/:token", element: <EarlyAccessPage /> },
  {
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <MapPage /> },
      { path: "circles", element: <CirclesPage /> },
      { path: "circles/:circleId", element: <CirclePage /> },
      { path: "circles/:circleId/places", element: <PlacesPage /> },
      { path: "circles/:circleId/notifications", element: <NotificationsPage /> },
      { path: "circles/:circleId/members/:userId/history", element: <HistoryPage /> },
      { path: "circles/:circleId/members/:userId/driving", element: <DrivingPage /> },
      { path: "chats", element: <ChatsPage /> },
      { path: "chats/:conversationId", element: <ChatsPage /> },
      { path: "join/:code", element: <JoinPage /> },
      { path: "account", element: <AccountPage /> },
    ],
  },
  { path: "*", element: <NotFoundPage /> },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </QueryClientProvider>
  );
}
